import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, Clock, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerIcon = L.icon({ iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png', iconSize: [25, 41], iconAnchor: [12, 41] });
const destIcon = L.icon({ iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png', iconSize: [25, 41], iconAnchor: [12, 41] });
const arrivedIcon = L.divIcon({ className: 'gj-arrived-pulse', html: '<div class="gj-pulse-ring"></div><div class="gj-pulse-dot"></div>', iconSize: [24, 24], iconAnchor: [12, 12] });

// Live route map shown to BOTH roles while the provider is on the way.
// - Provider: shares its GPS, sees its own position + route to the client's pin.
// - Client: watches the provider's position moving toward the service pin.
const LiveTrackingMap = ({ booking, effectiveRole, height = '15rem', shareLocation = true, testId = 'live-tracking-map' }) => {
  const bookingId = booking?.id;
  const isProvider = effectiveRole === 'provider';
  const [t, setT] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [clientPos, setClientPos] = useState(null);
  const [eta, setEta] = useState(null);

  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const provMarker = useRef(null);
  const destMarker = useRef(null);
  const routeLine = useRef(null);
  const lastPost = useRef(0);
  const arrived = !!(t && t.arrived_at);

  const fetchTracking = useCallback(async () => {
    try { const r = await axios.get(`${API}/bookings/${bookingId}/tracking`); setT(r.data); } catch (_) {}
  }, [bookingId]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);

  // Poll for latest tracking state (provider location moves) until arrival/start.
  useEffect(() => {
    if (!t || t.arrived_at || t.job_started_at || t.job_ended_at) return;
    const iv = setInterval(fetchTracking, 12000);
    return () => clearInterval(iv);
  }, [t, fetchTracking]);

  // Provider: watch own GPS and share it (throttled) while on the way — stops on arrival.
  useEffect(() => {
    if (!isProvider || !navigator.geolocation || !t || !t.on_my_way_at || t.arrived_at || t.job_started_at || t.job_ended_at) return;
    const onPos = (pos) => {
      const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      setMyPos(p);
      if (shareLocation && Date.now() - lastPost.current > 12000) {
        lastPost.current = Date.now();
        axios.post(`${API}/bookings/${bookingId}/location`, p).catch(() => {});
      }
    };
    const id = navigator.geolocation.watchPosition(onPos, () => {}, { enableHighAccuracy: true, maximumAge: 5000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [isProvider, t, bookingId, shareLocation]);

  // Client: grab own position as a fallback destination if the booking has no pin.
  useEffect(() => {
    if (isProvider || !t || !t.on_my_way_at || t.service_lat != null || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setClientPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, { enableHighAccuracy: true }
    );
  }, [isProvider, t]);

  const dest = useMemo(() => {
    if (t && t.service_lat != null) return { lat: t.service_lat, lng: t.service_lng };
    return clientPos;
  }, [t, clientPos]);

  const providerPos = useMemo(() => {
    if (isProvider) return myPos;
    if (t && t.provider_lat != null) return { lat: t.provider_lat, lng: t.provider_lng };
    return null;
  }, [isProvider, myPos, t]);

  // Render / update the Leaflet map.
  useEffect(() => {
    if (!mapRef.current) return;
    if (!providerPos && !dest) return;
    const center = providerPos || dest;
    if (!mapObj.current) {
      mapObj.current = L.map(mapRef.current, { scrollWheelZoom: false }).setView([center.lat, center.lng], 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '© OpenStreetMap' }).addTo(mapObj.current);
    }
    if (providerPos) {
      if (!provMarker.current) provMarker.current = L.marker([providerPos.lat, providerPos.lng], { icon: providerIcon }).addTo(mapObj.current).bindPopup(isProvider ? 'You' : (t?.provider_name || 'Provider'));
      else provMarker.current.setLatLng([providerPos.lat, providerPos.lng]);
    }
    if (dest) {
      if (!destMarker.current) destMarker.current = L.marker([dest.lat, dest.lng], { icon: arrived ? arrivedIcon : destIcon }).addTo(mapObj.current).bindPopup('Service location');
      else { destMarker.current.setLatLng([dest.lat, dest.lng]); destMarker.current.setIcon(arrived ? arrivedIcon : destIcon); }
    }
    if (arrived) {
      // Trip complete: drop the route line and focus the arrival pin.
      if (routeLine.current) { mapObj.current.removeLayer(routeLine.current); routeLine.current = null; }
      if (dest) mapObj.current.setView([dest.lat, dest.lng], 16);
    } else if (providerPos && dest) {
      try { mapObj.current.fitBounds(L.latLngBounds([[providerPos.lat, providerPos.lng], [dest.lat, dest.lng]]).pad(0.35)); } catch (_) {}
    } else {
      mapObj.current.setView([center.lat, center.lng]);
    }
    setTimeout(() => mapObj.current && mapObj.current.invalidateSize(), 150);
  }, [providerPos, dest, isProvider, t, arrived]);

  // OSRM route polyline + ETA between provider and destination (skipped once arrived).
  useEffect(() => {
    if (arrived || !providerPos || !dest || !mapObj.current) return;
    let active = true;
    (async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${providerPos.lng},${providerPos.lat};${dest.lng},${dest.lat}?overview=full&geometries=geojson`;
        const r = await fetch(url); const j = await r.json();
        if (!active || !j.routes || !j.routes[0]) return;
        const route = j.routes[0];
        setEta({ km: route.distance / 1000, min: route.duration / 60 });
        const coords = route.geometry.coordinates.map((c) => [c[1], c[0]]);
        if (routeLine.current) { mapObj.current.removeLayer(routeLine.current); }
        routeLine.current = L.polyline(coords, { color: '#0284c7', weight: 4, opacity: 0.8 }).addTo(mapObj.current);
      } catch (_) {}
    })();
    return () => { active = false; };
  }, [providerPos, dest, arrived]);

  useEffect(() => () => { if (mapObj.current) { mapObj.current.remove(); mapObj.current = null; provMarker.current = null; destMarker.current = null; routeLine.current = null; } }, []);

  const gmapsUrl = useMemo(() => {
    if (!dest) return null;
    const base = `https://www.google.com/maps/dir/?api=1&destination=${dest.lat},${dest.lng}&travelmode=driving`;
    return providerPos ? `${base}&origin=${providerPos.lat},${providerPos.lng}` : base;
  }, [dest, providerPos]);

  return (
    <div data-testid={testId}>
      {arrived && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-green-100 border border-green-200 px-3 py-2 text-sm font-semibold text-green-800" data-testid={`${testId}-arrived-banner`}>
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-600"></span>
          </span>
          {isProvider ? "You've arrived at the location." : 'Your provider has arrived! 🎉'}
        </div>
      )}
      {!arrived && eta && (
        <div className="flex items-center gap-4 mb-2 text-sm" data-testid={`${testId}-eta`}>
          <span className="inline-flex items-center gap-1 text-sky-700 font-semibold"><Clock className="w-4 h-4" /> ~{Math.round(eta.min)} min</span>
          <span className="text-gray-600">{eta.km.toFixed(1)} km away</span>
        </div>
      )}
      {(providerPos || dest) ? (
        <div ref={mapRef} style={{ height }} className="w-full rounded-lg overflow-hidden border border-gray-200 z-0" data-testid={`${testId}-canvas`} />
      ) : (
        <p className="text-sm text-gray-500" data-testid={`${testId}-waiting`}>
          {isProvider ? 'Getting your location…' : "Waiting for your provider's live location…"}
        </p>
      )}
      {!arrived && gmapsUrl && (
        <a
          href={gmapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full py-2.5 bg-sky-600 text-white rounded-lg text-sm font-semibold hover:bg-sky-700"
          data-testid={`${testId}-navigate-btn`}
        >
          {isProvider ? <Navigation className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
          {isProvider ? 'Navigate in Google Maps' : 'Open route in Google Maps'}
        </a>
      )}
    </div>
  );
};

export default LiveTrackingMap;
